import { Box } from '@chakra-ui/layout';
import { CardBody, Image } from '@chakra-ui/react';
import { useRef } from 'react';

import type { ChartType } from '@revelio/core';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT } from '../../../constants/panelWidths';
import { useAdaptiveChartType } from '../../../hooks/useAdaptiveChartType';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { MultiMetricDataProvider } from '../../DataProvider/DataProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { isCompositionSecondaryDimension } from '../../PageConfigProvider/types/dimension';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import { AiSummaryChart } from '../AiSummary/AiSummaryChart';
import {
  ChartActionMenu,
  ChartHeader,
  ChartHeaderAction,
  ChartHeaderTitleInfo,
  ChartSwitchers,
} from '../ChartHeader';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { EntitySummary } from '../EntitySummary/EntitySummary';
import { MapChartSelector } from '../Map/MapChartSelector';
import { MixedStatsChart } from '../MixedStats/MixedStats';
import { SentimentEffectBarChart } from '../SentimentEffectBar';
import { SentimentReview } from '../SentimentReview';
import { ShareOfSegmentChart } from '../ShareOfSegment/ShareOfSegment';
import { TextChartCardContentWithFilters } from '../TextChart/TextChartCardContent';
import { ChartCard, type ChartProps } from './Chart';

type SpecialChartType = Extract<
  ChartType,
  | 'entity-summary'
  | 'mixed-stats'
  | 'share-of-segment'
  | 'sentiment_review'
  | 'sentiment_effect_bar'
  | 'map'
  | 'text'
  | 'ai-summary'
  | 'image'
>;

export const SpecialChartTypes: Record<SpecialChartType, boolean> = {
  'entity-summary': true,
  'mixed-stats': true,
  'share-of-segment': true,
  sentiment_review: true,
  sentiment_effect_bar: true,
  map: true,
  text: true,
  'ai-summary': true,
  image: true,
};

export const isSpecialChartType = (
  chartType: ChartType
): chartType is SpecialChartType =>
  SpecialChartTypes[chartType as SpecialChartType];

type SpecialChartProps = Pick<
  ChartProps,
  'editMode' | 'item' | 'headerSize' | 'primaryFilter' | 'onClick'
> & {
  chartTitle: string;
  metric?: ReturnType<typeof usePlotMetric>;
};

export const SpecialChart = ({
  editMode,
  item,
  headerSize,
  primaryFilter,
  chartTitle,
  metric,
  onClick,
}: SpecialChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const chartType = item?.plot?.chartType;
  const isMapChart = chartType === 'map';
  const gaNonMapInlinePlotHeight = GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;
  const showActionMenu =
    Boolean(
      chartType &&
        (metric || chartType === 'text') &&
        chartType !== 'sentiment_review'
    ) && !isGlobalAiChartPreview;

  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const cardBodyHeight = (() => {
    if (isGlobalAiChartPreview && !isMapChart) {
      return 'auto';
    }
    if (isGlobalAiChartPreview && isMapChart) {
      return GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;
    }
    if (isMapChart) {
      return '100%';
    }
    return 'calc(100% - 40px)';
  })();
  const cardBodyOverflow = isGlobalAiChartPreview ? 'visible' : undefined;
  const cardBodyMinH =
    isGlobalAiChartPreview && chartType !== 'text' && !isMapChart
      ? 'min-content'
      : undefined;

  const chartBodyContent = (() => {
    if (
      isGlobalAiChartPreview &&
      chartType !== undefined &&
      chartType !== 'text' &&
      !isMapChart
    ) {
      return (
        <Box
          w="100%"
          flexShrink={0}
          minH={gaNonMapInlinePlotHeight}
          h={gaNonMapInlinePlotHeight}
        >
          <SpecialChartSelector />
        </Box>
      );
    }
    if (isGlobalAiChartPreview && isMapChart) {
      return (
        <Box
          w="100%"
          h={GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT}
          minH={GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT}
          position="relative"
        >
          <SpecialChartSelector />
        </Box>
      );
    }
    return <SpecialChartSelector />;
  })();

  return (
    <ChartCard
      onClick={onClick}
      {...(isGlobalAiChartPreview && isMapChart
        ? {
            minH: GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
          }
        : {})}
    >
      {!isMapChart && (
        <ChartHeader
          headerSize={headerSize}
          {...(showActionMenu && { paddingRight: '0.5rem' })}
        >
          <ChartHeaderTitleInfo
            title={chartTitle}
            {...(item?.info &&
              item?.id !== 'role-overview-summary' && {
                info: item.info,
              })}
          />
          <Box maxWidth="60%">
            <ChartHeaderAction>
              <ChartSwitchers
                primaryFilterId={primaryFilter?.id}
                isSingleEntityChart={showPrimaryEntitySwitcher}
                {...(showActionMenu && { width: 'calc(100% - 20px)' })}
              />
              {showActionMenu && (
                <ChartActionMenu
                  printRef={printRef}
                  item={item}
                  metric={metric}
                  title={chartTitle}
                  primaryFilterId={primaryFilter?.id}
                  slim={headerSize === 'sm'}
                />
              )}
            </ChartHeaderAction>
          </Box>
        </ChartHeader>
      )}
      <CardBody
        ref={printRef}
        data-testid="card-body"
        padding="0.5rem 14px 0.5rem"
        height={cardBodyHeight}
        {...(cardBodyOverflow !== undefined
          ? { overflow: cardBodyOverflow }
          : {})}
        {...(cardBodyMinH !== undefined ? { minH: cardBodyMinH } : {})}
        {...(isGlobalAiChartPreview ? { flexShrink: 0 } : {})}
        {...((editMode ||
          showPrimaryEntitySwitcher ||
          isGlobalAiChartPreview) && {
          position: 'relative',
        })}
        {...(isMapChart && { padding: '1rem' })}
      >
        {chartBodyContent}
      </CardBody>
    </ChartCard>
  );
};

export const SpecialChartSelector = () => {
  const { primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { item } = useChart();
  const metric = usePlotMetric();

  const primaryFilterId = primaryFilter?.id;

  const { chartType: hookChartType } = useAdaptiveChartType(item);
  const { chartType: baseChartType, secondaryDimension } = item?.plot ?? {};
  const chartType = hookChartType ?? baseChartType;

  if (!chartType || !item) {
    return <ChartNotSupported />;
  }

  /** ================================
   * Special Charts
   * ================================
   * These are charts with specific use cases and not generic */
  /** ======== Entity Overview Summary ========
   * Company Overview & Role Overview */
  if (chartType === 'entity-summary') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <EntitySummary primaryDimension={primaryFilterId} />;
  }
  /** ======== Mixed Stats ========
   * Company Overview & Role Overview */
  if (chartType === 'mixed-stats') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <MixedStatsChart primaryDimension={primaryFilterId} />;
  }
  /** ======== Share of Segment ======== */
  if (
    chartType === 'share-of-segment' &&
    (metric === 'share_of_headcount' || metric === 'headcount') &&
    secondaryDimension &&
    isCompositionSecondaryDimension(secondaryDimension)
  ) {
    if (!primaryFilterId) return <ChartNotSupported />;
    return (
      <ShareOfSegmentChart
        primaryDimension={primaryFilterId}
        secondaryDimension={secondaryDimension}
        plotId={item.id}
      />
    );
  }
  /** ======== Sentiment Review ======== */
  if (chartType === 'sentiment_review') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <SentimentReview primaryDimension={primaryFilterId} />;
  }
  /** ======== Sentiment Effect Bar ======== */
  if (chartType === 'sentiment_effect_bar') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <SentimentEffectBarChart primaryDimension={primaryFilterId} />;
  }

  /** ======== Generic Map ======== */
  if (chartType === 'map' && metric) {
    return (
      <MapChartSelector
        primaryDimension={primaryFilterId}
        metric={metric}
        mapOptions={item?.plot?.mapOptions ?? {}}
      />
    );
  }

  if (chartType === 'text') {
    return (
      <MultiMetricDataProvider
        item={item}
        chartType="text"
        primaryDimension={primaryFilterId}
      >
        {({ data, status }) => (
          <TextChartCardContentWithFilters
            data={data}
            status={status}
            item={item}
            pageConfig={pageConfig}
          />
        )}
      </MultiMetricDataProvider>
    );
  }

  if (chartType === 'ai-summary') {
    return <AiSummaryChart />;
  }

  if (chartType === 'image') {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        width="100%"
        height="100%"
      >
        <Image
          src={item?.plot?.imageSrc}
          alt="Image"
          maxW="100%"
          maxH="100%"
          objectFit="contain"
        />
      </Box>
    );
  }

  return <ChartNotSupported />;
};
